import * as nls from '@vscode-alt/monaco-editor/esm/vs/nls';
import * as dom from '@vscode-alt/monaco-editor/esm/vs/base/browser/dom';
import { StandardKeyboardEvent } from '@vscode-alt/monaco-editor/esm/vs/base/browser/keyboardEvent';
import { Keybinding, ResolvedKeybinding, KeyCode, KeyMod } from '@vscode-alt/monaco-editor/esm/vs/base/common/keyCodes';
import { KeybindingParser } from '@vscode-alt/monaco-editor/esm/vs/base/common/keybindingParser';
import { OS, OperatingSystem, isMacintosh } from '@vscode-alt/monaco-editor/esm/vs/base/common/platform';
import { IKeyboardEvent, IUserFriendlyKeybinding } from '@vscode-alt/monaco-editor/esm/vs/platform/keybinding/common/keybinding';
import { IKeybindingItem, IKeybindingRule2, KeybindingWeight, KeybindingsRegistry } from '@vscode-alt/monaco-editor/esm/vs/platform/keybinding/common/keybindingsRegistry';
import { ResolvedKeybindingItem } from '@vscode-alt/monaco-editor/esm/vs/platform/keybinding/common/resolvedKeybindingItem';
import { ScanCode, ScanCodeUtils, IMMUTABLE_CODE_TO_KEY_CODE } from '@vscode-alt/monaco-editor/esm/vs/base/common/scanCode';
import { Disposable } from '@vscode-alt/monaco-editor/esm/vs/base/common/lifecycle';
import { RunOnceScheduler } from '@vscode-alt/monaco-editor/esm/vs/base/common/async';
import { Emitter, Event } from '@vscode-alt/monaco-editor/esm/vs/base/common/event';
import * as objects from '@vscode-alt/monaco-editor/esm/vs/base/common/objects';
import { parse } from '@vscode-alt/monaco-editor/esm/vs/base/common/json';
import { isArray } from '@vscode-alt/monaco-editor/esm/vs/base/common/types';
import { URI } from '@vscode-alt/monaco-editor/esm/vs/base/common/uri';

import { inject, injectable, postConstruct } from 'inversify';
import { ApolloClient } from 'apollo-client';
import { InMemoryCache } from 'apollo-cache-inmemory';
import {
    IClientWorkspaceContextService as IWorkspaceContextService, IKeyboardMapper, IKeymapService,
    ILifecycleService, LifecyclePhase, ClientTypes, IClientConfigurationService, IEnvironmentService,
    INotificationService, ICommandService, IKeybindings, KeybindingsFragmentDoc, IKeybindingService,
    IKeybindingEvent, IKeybindingSource as KeybindingSource, IContextKeyService, CacheTypenames,
} from '@workbench-stack/core';
import { ClientTypes as FilesTypes, ITextFileService } from '@files-stack/core';
import { ClientTypes as CommonTypes } from '@common-stack/client-react';
import { IKeyboard, INavigatorWithKeyboard } from './keymap-service';
import { getDispatchConfig } from './keyboardMapper';
import { KeybindingIO } from './keyboardMapper';
import { KeybindingResolver } from '@workbench-stack/platform-browser/lib/services/keybinding/keybinding-resolver';
import { AbstractKeybindingService } from '@workbench-stack/platform-browser/lib/services/keybinding/abstract-keybinding-service';

interface ContributedKeyBinding {
    command: string;
    args?: any;
    key: string;
    when?: string;
    mac?: string;
    linux?: string;
    win?: string;
}

function isContributedKeyBindingsArray(thing: ContributedKeyBinding | ContributedKeyBinding[]): thing is ContributedKeyBinding[] {
    return Array.isArray(thing);
}

function isValidContributedKeyBinding(keyBinding: ContributedKeyBinding, rejects: string[]): boolean {
    if (!keyBinding) {
        rejects.push(nls.localize('nonempty', 'expected non-empty value.'));
        return false;
    }
    if (typeof keyBinding.command !== 'string') {
        rejects.push(nls.localize('requirestring', 'property `{0}` is mandatory and must be of type `string`', 'command'));
        return false;
    }
    if (keyBinding.key && typeof keyBinding.key !== 'string') {
        rejects.push(nls.localize('optstring', 'property `{0}` can be omitted or must be of type `string`', 'key'));
        return false;
    }
    if (keyBinding.when && typeof keyBinding.when !== 'string') {
        rejects.push(nls.localize('optstring', 'property `{0}` can be omitted or must be of type `string`', 'when'));
        return false;
    }
    if (keyBinding.mac && typeof keyBinding.mac !== 'string') {
        rejects.push(nls.localize('optstring', 'property `{0}` can be omitted or must be of type `string`', 'mac'));
        return false;
    }
    if (keyBinding.linux && typeof keyBinding.linux !== 'string') {
        rejects.push(nls.localize('optstring', 'property `{0}` can be omitted or must be of type `string`', 'linux'));
        return false;
    }
    if (keyBinding.win && typeof keyBinding.win !== 'string') {
        rejects.push(nls.localize('optstring', 'property `{0}` can be omitted or must be of type `string`', 'win'));
        return false;
    }
    return true;
}

const otherMacNumpadMapping = new Map<ScanCode, KeyCode>();
otherMacNumpadMapping.set(ScanCode.Numpad1, KeyCode.KEY_1);
otherMacNumpadMapping.set(ScanCode.Numpad2, KeyCode.KEY_2);
otherMacNumpadMapping.set(ScanCode.Numpad3, KeyCode.KEY_3);
otherMacNumpadMapping.set(ScanCode.Numpad4, KeyCode.KEY_4);
otherMacNumpadMapping.set(ScanCode.Numpad5, KeyCode.KEY_5);
otherMacNumpadMapping.set(ScanCode.Numpad6, KeyCode.KEY_6);
otherMacNumpadMapping.set(ScanCode.Numpad7, KeyCode.KEY_7);
otherMacNumpadMapping.set(ScanCode.Numpad8, KeyCode.KEY_8);
otherMacNumpadMapping.set(ScanCode.Numpad9, KeyCode.KEY_9);
otherMacNumpadMapping.set(ScanCode.Numpad0, KeyCode.KEY_0);


const NUMPAD_PRINTABLE_SCANCODES = [
    ScanCode.NumpadDivide,
    ScanCode.NumpadMultiply,
    ScanCode.NumpadSubtract,
    ScanCode.NumpadAdd,
    ScanCode.Numpad1,
    ScanCode.Numpad2,
    ScanCode.Numpad3,
    ScanCode.Numpad4,
    ScanCode.Numpad5,
    ScanCode.Numpad6,
    ScanCode.Numpad7,
    ScanCode.Numpad8,
    ScanCode.Numpad9,
    ScanCode.Numpad0,
    ScanCode.NumpadDecimal,
];

@injectable()
export class WorkbenchKeybindingService extends AbstractKeybindingService implements IKeybindingService {

    private _keyboardMapper: IKeyboardMapper;
    private _cachedResolver: KeybindingResolver | null;
    private userKeybindings: UserKeybindings;
    // private readonly _contributions: KeybindingsSchemaContribution[] = [];

    constructor(
        @inject(CommonTypes.ApolloClient)
        public client: ApolloClient<any>,
        @inject(CommonTypes.InMemoryCache)
        public cache: InMemoryCache,
        // @inject(ClientTypes.IWorkspaceContextService)
        // protected readonly contextService: IWorkspaceContextService,
        @inject(ClientTypes.IContextKeyService)
        private contextKeyService: IContextKeyService,
        @inject(ClientTypes.ICommandService)
        public commandService: ICommandService,
        @inject(ClientTypes.INotificationService)
        public notificationService: INotificationService,
        @inject(ClientTypes.ILifecycleService)
        private lifecycleService: ILifecycleService,
        @inject(ClientTypes.IEnvironmentService)
        public environmentService: IEnvironmentService,
        @inject(ClientTypes.IConfigurationService)
        public configurationService: IClientConfigurationService,
        @inject(FilesTypes.ITextFileService)
        private textFileService: ITextFileService,
        @inject(ClientTypes.IKeymapService)
        private readonly keymapService: IKeymapService,
    ) {
        super(contextKeyService, commandService, null, notificationService);

        // this.updateSchema()

        // keybindingsExtPoint.setHandler((extensions) => {
        //
        //     let keybindings: IKeybindingRule2[] = [];
        //     for (let extension of extensions) {
        //         this._handleKeybindingsExtensionPointUser(extension.description.isBuiltin, extension.value, extension.collector, keybindings);
        //     }
        //
        //     KeybindingsRegistry.setExtensionKeybindings(keybindings);
        //     this.updateResolver({ source: KeybindingSource.Default });
        // });

        // this.updateSchema();
        // this._register(extensionService.onDidRegisterExtensions(() => this.updateSchema()));
        this.lifecycleService.when(LifecyclePhase.Restored).then(() => {
            this._register(dom.addDisposableListener(window, dom.EventType.KEY_DOWN, (e: KeyboardEvent) => {
                let keyEvent = new StandardKeyboardEvent(e);
                let shouldPreventDefault = this._dispatch(keyEvent, keyEvent.target);
                if (shouldPreventDefault) {
                    keyEvent.preventDefault();
                }
            }));
        });

        // let data = this.keymapService.getCurrentKeyboardLayout();
        // telemetryService.publicLog('keyboardLayout', {
        //     currentKeyboardLayout: data,
        // });

        // this._register(browser.onDidChangeFullscreen(() => {
        //     const keyboard: IKeyboard | null = (<INavigatorWithKeyboard>navigator).keyboard;
        //
        //     if (BrowserFeatures.keyboard === KeyboardSupport.None) {
        //         return;
        //     }
        //
        //     if (browser.isFullscreen()) {
        //         keyboard?.lock(['Escape']);
        //     } else {
        //         keyboard?.unlock();
        //     }
        //
        //     // update resolver which will bring back all unbound keyboard shortcuts
        //     this._cachedResolver = null;
        //     this._onDidUpdateKeybindings.fire({ source: KeybindingSource.User });
        // }));
    }

    @postConstruct()
    private async postSetup() {
        await this.lifecycleService.when(LifecyclePhase.Restored).then(() => {
            // setTimeout(() => {
                const keybindingResource = URI.file(this.environmentService.appKeybindingsPath);
                this.userKeybindings = this._register(new UserKeybindings(this.client, this.cache, this.textFileService, keybindingResource));
                this.userKeybindings.initialize().then(() => {
                    if (this.userKeybindings.keybindings.length) {
                        this.updateResolver({ source: KeybindingSource.User });
                    }
                });
                this._register(this.userKeybindings.onDidChange(() => {
                    this.updateResolver({
                        source: KeybindingSource.User,
                        keybindings: this.userKeybindings.keybindings,
                    });
                }));

                let dispatchConfig = getDispatchConfig(this.configurationService);
                this.configurationService.onDidChangeConfiguration((e) => {
                    let newDispatchConfig = getDispatchConfig(this.configurationService);
                    if (dispatchConfig === newDispatchConfig) {
                        return;
                    }

                    dispatchConfig = newDispatchConfig;
                    this._keyboardMapper = this.keymapService.getKeyboardMapper(dispatchConfig);
                    this.updateResolver({ source: KeybindingSource.Default});
                });

                this._keyboardMapper = this.keymapService.getKeyboardMapper(dispatchConfig);
                this.keymapService.onDidChangeKeyboardMapper(() => {
                    this._keyboardMapper = this.keymapService.getKeyboardMapper(dispatchConfig);
                    this.updateResolver({source: KeybindingSource.Default});
                });

                this._cachedResolver = null;
            // }, 5000);
        });
    }

    public registerSchemaContribution(contribution/*: KeybindingsSchemaContribution*/): void {
        // this._contributions.push(contribution);
        // if (contribution.onDidChange) {
        //     this._register(contribution.onDidChange(() => this.updateSchema()));
        // }
        // this.updateSchema();
    }

    // private updateSchema() {
    //     updateSchema(flatten(this._contributions.map(x => x.getSchemaAdditions())));
    // }

    public _dumpDebugInfo(): string {
        const layoutInfo = JSON.stringify(this.keymapService.getCurrentKeyboardLayout(), null, '\t');
        const mapperInfo = this._keyboardMapper.dumpDebugInfo();
        const rawMapping = JSON.stringify(this.keymapService.getRawKeyboardMapping(), null, '\t');
        return `Layout info:\n${layoutInfo}\n${mapperInfo}\n\nRaw mapping:\n${rawMapping}`;
    }

    public _dumpDebugInfoJSON(): string {
        const info = {
            layout: this.keymapService.getCurrentKeyboardLayout(),
            rawMapping: this.keymapService.getRawKeyboardMapping(),
        };
        return JSON.stringify(info, null, '\t');
    }

    public customKeybindingsCount(): number {
        return this.userKeybindings.keybindings.length;
    }

    private updateResolver(event: IKeybindingEvent): void {
        this._cachedResolver = null;
        this._onDidUpdateKeybindings.fire(event);
    }

    protected _getResolver(): KeybindingResolver {
        if (!this._cachedResolver) {
            const defaults = this._resolveKeybindingItems(KeybindingsRegistry.getDefaultKeybindings(), true);
            const overrides = this._resolveUserKeybindingItems(this.userKeybindings.keybindings.map((k) => KeybindingIO.readUserKeybindingItem(k)), false);
            this._cachedResolver = new KeybindingResolver(defaults, overrides);
        }
        return this._cachedResolver;
    }

    protected _documentHasFocus(): boolean {
        // it is possible that the document has lost focus, but the
        // window is still focused, e.g. when a <webview> element
        // has focus
        // return this.hostService.hasFocus;
        return;
    }

    private _resolveKeybindingItems(items: IKeybindingItem[], isDefault: boolean): ResolvedKeybindingItem[] {
        let result: ResolvedKeybindingItem[] = [], resultLen = 0;
        for (const item of items) {
            const when = item.when || undefined;
            const keybinding = item.keybinding;
            if (!keybinding) {
                // This might be a removal keybinding item in user settings => accept it
				result[resultLen++] = new ResolvedKeybindingItem(undefined, item.command, item.commandArgs, when, isDefault, item.extensionId, item.isBuiltinExtension);
            } else {
                if (this._assertBrowserConflicts(keybinding, item.command)) {
                    continue;
                }

                const resolvedKeybindings = this.resolveKeybinding(keybinding);
                for (let i = resolvedKeybindings.length - 1; i >= 0; i--) {
                    const resolvedKeybinding = resolvedKeybindings[i];
					result[resultLen++] = new ResolvedKeybindingItem(resolvedKeybinding, item.command, item.commandArgs, when, isDefault, item.extensionId, item.isBuiltinExtension);
                }
            }
        }

        return result;
    }

    private _resolveUserKeybindingItems(items/*: IUserKeybindingItem[]*/, isDefault: boolean): ResolvedKeybindingItem[] {
        let result: ResolvedKeybindingItem[] = [], resultLen = 0;
        for (const item of items) {
            const when = item.when || undefined;
            const parts = item.parts;
            if (parts.length === 0) {
                // This might be a removal keybinding item in user settings => accept it
				result[resultLen++] = new ResolvedKeybindingItem(undefined, item.command, item.commandArgs, when, isDefault, null, false);
            } else {
                const resolvedKeybindings = this._keyboardMapper.resolveUserBinding(parts);
                for (const resolvedKeybinding of resolvedKeybindings) {
					result[resultLen++] = new ResolvedKeybindingItem(resolvedKeybinding, item.command, item.commandArgs, when, isDefault, null, false);
                }
            }
        }

        return result;
    }

    private _assertBrowserConflicts(kb: Keybinding, commandId: string): boolean {
        // if (BrowserFeatures.keyboard === KeyboardSupport.Always) {
        //     return false;
        // }
        //
        // if (BrowserFeatures.keyboard === KeyboardSupport.FullScreen && browser.isFullscreen()) {
        //     return false;
        // }

        for (let part of kb.parts) {
            if (!part.metaKey && !part.altKey && !part.ctrlKey && !part.shiftKey) {
                continue;
            }

            // const modifiersMask = KeyMod.CtrlCmd | KeyMod.Alt | KeyMod.Shift;
            //
            // let partModifiersMask = 0;
            // if (part.metaKey) {
            //     partModifiersMask |= KeyMod.CtrlCmd;
            // }
            //
            // if (part.shiftKey) {
            //     partModifiersMask |= KeyMod.Shift;
            // }
            //
            // if (part.altKey) {
            //     partModifiersMask |= KeyMod.Alt;
            // }
            //
            // if (part.ctrlKey && OS === OperatingSystem.Macintosh) {
            //     partModifiersMask |= KeyMod.WinCtrl;
            // }
            //
            // if ((partModifiersMask & modifiersMask) === KeyMod.CtrlCmd && part.keyCode === KeyCode.KEY_W) {
            //     // console.warn('Ctrl/Cmd+W keybindings should not be used by default in web. Offender: ', kb.getHashCode(), ' for ', commandId);
            //
            //     return true;
            // }
            //
            // if ((partModifiersMask & modifiersMask) === KeyMod.CtrlCmd && part.keyCode === KeyCode.KEY_N) {
            //     // console.warn('Ctrl/Cmd+N keybindings should not be used by default in web. Offender: ', kb.getHashCode(), ' for ', commandId);
            //
            //     return true;
            // }
            //
            // if ((partModifiersMask & modifiersMask) === KeyMod.CtrlCmd && part.keyCode === KeyCode.KEY_T) {
            //     // console.warn('Ctrl/Cmd+T keybindings should not be used by default in web. Offender: ', kb.getHashCode(), ' for ', commandId);
            //
            //     return true;
            // }
            //
            // if ((partModifiersMask & modifiersMask) === (KeyMod.CtrlCmd | KeyMod.Alt) && (part.keyCode === KeyCode.LeftArrow || part.keyCode === KeyCode.RightArrow)) {
            //     // console.warn('Ctrl/Cmd+Arrow keybindings should not be used by default in web. Offender: ', kb.getHashCode(), ' for ', commandId);
            //
            //     return true;
            // }
            //
            // if ((partModifiersMask & modifiersMask) === KeyMod.CtrlCmd && part.keyCode >= KeyCode.KEY_0 && part.keyCode <= KeyCode.KEY_9) {
            //     // console.warn('Ctrl/Cmd+Num keybindings should not be used by default in web. Offender: ', kb.getHashCode(), ' for ', commandId);
            //
            //     return true;
            // }
        }
    //
        return false;
    }

    public resolveKeybinding(kb: Keybinding): ResolvedKeybinding[] {
        if (this._keyboardMapper) {
            return this._keyboardMapper.resolveKeybinding(kb);
        }
    }

    public resolveKeyboardEvent(keyboardEvent: IKeyboardEvent): ResolvedKeybinding {
        this.keymapService.validateCurrentKeyboardMapping(keyboardEvent);
        return this._keyboardMapper.resolveKeyboardEvent(keyboardEvent);
    }

    public resolveUserBinding(userBinding: string): ResolvedKeybinding[] {
        const parts = KeybindingParser.parseUserBinding(userBinding);
        return this._keyboardMapper.resolveUserBinding(parts);
    }

    // private _handleKeybindingsExtensionPointUser(isBuiltin: boolean, keybindings: ContributedKeyBinding | ContributedKeyBinding[],
    // collector: ExtensionMessageCollector, result: IKeybindingRule2[]): void {
    //     if (isContributedKeyBindingsArray(keybindings)) {
    //         for (let i = 0, len = keybindings.length; i < len; i++) {
    //             this._handleKeybinding(isBuiltin, i + 1, keybindings[i], collector, result);
    //         }
    //     } else {
    //         this._handleKeybinding(isBuiltin, 1, keybindings, collector, result);
    //     }
    // }

    // private _handleKeybinding(isBuiltin: boolean, idx: number, keybindings: ContributedKeyBinding, collector: ExtensionMessageCollector, result: IKeybindingRule2[]): void {
    //
    //     let rejects: string[] = [];
    //
    //     if (isValidContributedKeyBinding(keybindings, rejects)) {
    //         let rule = this._asCommandRule(isBuiltin, idx++, keybindings);
    //         if (rule) {
    //             result.push(rule);
    //         }
    //     }
    //
    //     if (rejects.length > 0) {
    //         collector.error(nls.localize(
    //             'invalid.keybindings',
    //             "Invalid `contributes.{0}`: {1}",
    //             keybindingsExtPoint.name,
    //             rejects.join('\n')
    //         ));
    //     }
    // }

    // private _asCommandRule(isBuiltin: boolean, idx: number, binding: ContributedKeyBinding): IKeybindingRule2 | undefined {
    //
    //     let { command, args, when, key, mac, linux, win } = binding;
    //
    //     let weight: number;
    //     if (isBuiltin) {
    //         weight = KeybindingWeight.BuiltinExtension + idx;
    //     } else {
    //         weight = KeybindingWeight.ExternalExtension + idx;
    //     }
    //
    //     let commandAction = MenuRegistry.getCommand(command);
    //     let precondition = commandAction && commandAction.precondition;
    //     let fullWhen: ContextKeyExpression | undefined;
    //     if (when && precondition) {
    //         fullWhen = ContextKeyExpr.and(precondition, ContextKeyExpr.deserialize(when));
    //     } else if (when) {
    //         fullWhen = ContextKeyExpr.deserialize(when);
    //     } else if (precondition) {
    //         fullWhen = precondition;
    //     }
    //
    //     let desc: IKeybindingRule2 = {
    //         id: command,
    //         args,
    //         when: fullWhen,
    //         weight: weight,
    //         primary: KeybindingParser.parseKeybinding(key, OS),
    //         mac: mac ? { primary: KeybindingParser.parseKeybinding(mac, OS) } : null,
    //         linux: linux ? { primary: KeybindingParser.parseKeybinding(linux, OS) } : null,
    //         win: win ? { primary: KeybindingParser.parseKeybinding(win, OS) } : null
    //     };
    //
    //     if (!desc.primary && !desc.mac && !desc.linux && !desc.win) {
    //         return undefined;
    //     }
    //
    //     return desc;
    // }

    public getDefaultKeybindingsContent(): string {
        const resolver = this._getResolver();
        const defaultKeybindings = resolver.getDefaultKeybindings();
        const boundCommands = resolver.getDefaultBoundCommands();
        return (
            WorkbenchKeybindingService._getDefaultKeybindings(defaultKeybindings)
            + '\n\n'
            + WorkbenchKeybindingService._getAllCommandsAsComment(boundCommands)
        );
    }

    private static _getDefaultKeybindings(defaultKeybindings: readonly ResolvedKeybindingItem[]): string {
        // let out = new OutputBuilder();
        // out.writeLine('[');

        let lastIndex = defaultKeybindings.length - 1;
        defaultKeybindings.forEach((k, index) => {
            // KeybindingIO.writeKeybindingItem(out, k);
            if (index !== lastIndex) {
                // out.writeLine(',');
            } else {
                // out.writeLine();
            }
        });
        // out.writeLine(']');
        // return out.toString();
        return;
    }

    private static _getAllCommandsAsComment(boundCommands: Map<string, boolean>): string {
        const unboundCommands = KeybindingResolver.getAllUnboundCommands(boundCommands);
        let pretty = unboundCommands.sort().join('\n// - ');
        return '// ' + nls.localize('unboundCommands', 'Here are other available commands: ') + '\n// - ' + pretty;
    }

    public mightProducePrintableCharacter(event: IKeyboardEvent): boolean {
        if (event.ctrlKey || event.metaKey || event.altKey) {
            // ignore ctrl/cmd/alt-combination but not shift-combinatios
            return false;
        }
        const code = ScanCodeUtils.toEnum(event.code);

        if (NUMPAD_PRINTABLE_SCANCODES.indexOf(code) !== -1) {
            // This is a numpad key that might produce a printable character based on NumLock.
            // Let's check if NumLock is on or off based on the event's keyCode.
            // e.g.
            // - when NumLock is off, ScanCode.Numpad4 produces KeyCode.LeftArrow
            // - when NumLock is on, ScanCode.Numpad4 produces KeyCode.NUMPAD_4
            // However, ScanCode.NumpadAdd always produces KeyCode.NUMPAD_ADD
            if (event.keyCode === IMMUTABLE_CODE_TO_KEY_CODE[code]) {
                // NumLock is on or this is /, *, -, + on the numpad
                return true;
            }
            if (isMacintosh && event.keyCode === otherMacNumpadMapping.get(code)) {
                // on macOS, the numpad keys can also map to keys 1 - 0.
                return true;
            }
            return false;
        }

        const keycode = IMMUTABLE_CODE_TO_KEY_CODE[code];
        if (keycode !== -1) {
            // https://github.com/microsoft/vscode/issues/74934
            return false;
        }
        // consult the KeyboardMapperFactory to check the given event for
        // a printable value.
        const mapping = this.keymapService.getRawKeyboardMapping();
        if (!mapping) {
            return false;
        }
        const keyInfo = mapping[event.code];
        if (!keyInfo) {
            return false;
        }
        if (!keyInfo.value || /\s/.test(keyInfo.value)) {
            return false;
        }
        return true;
    }
}

class UserKeybindings extends Disposable {

    private _keybindings: IUserFriendlyKeybinding[] = [];
    get keybindings(): IUserFriendlyKeybinding[] { return this._keybindings; }

    private readonly reloadConfigurationScheduler: RunOnceScheduler;

    private readonly _onDidChange: Emitter<void> = this._register(new Emitter<void>());
    public readonly onDidChange: Event<void> = this._onDidChange.event;

    constructor(
        @inject(CommonTypes.ApolloClient)
        public client: ApolloClient<any>,
        @inject(CommonTypes.InMemoryCache)
        public cache: InMemoryCache,
        @inject(FilesTypes.ITextFileService)
        private textFileService: ITextFileService,
        private readonly keybindingsResource: URI,
    ) {
        super();

        this.reloadConfigurationScheduler = this._register(new RunOnceScheduler(() => this.reload().then(changed => {
            if (changed) {
                this._onDidChange.fire();
            }
        }), 50));
        // this._register(Event.filter(this.fileService.onDidFilesChange, e => e.contains(this.keybindingsResource))(() => this.reloadConfigurationScheduler.schedule()));
    }

    public async initialize(): Promise<void> {
        await this.reload();
    }

    private async reload(): Promise<boolean> {
        const existing = this._keybindings;
        try {
            //@sri changed due too set keybindings

            const content = await this.textFileService.read(this.keybindingsResource);
            const value = parse(content.value.toString());
            this._keybindings = isArray(value) ? value : [];
        } catch (e) {
            this._keybindings = [];
        }
        return existing ? !objects.equals(existing, this._keybindings) : true;
    }
}
